import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { DataManager } from "@/components/DataManager";
import { PageHeader, StatusBadge } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Users, Lock, Eye, EyeOff, Loader2, ShieldCheck, KeyRound, Sparkles, Plane } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import OnboardingWizard from "@/components/OnboardingWizard";

const fields = [
  { name: "name", label: "Full Name", required: true },
  { name: "title", label: "Title / Position" },
  { name: "employee_role", label: "Role", type: "select", options: ["Pharmacist", "Technician", "Intern", "Administrative"] },
  { name: "department", label: "Department", type: "select", options: ["Dispensing", "Sterile Lab", "Non-Sterile Lab", "Administrative", "Delivery"] },
  { name: "email", label: "Email" },
  { name: "phone", label: "Phone", mono: true },
  { name: "address", label: "Home Address", full: true },
  { name: "hire_date", label: "Hire Date", type: "date" },
  { name: "license_number", label: "License #", mono: true },
  { name: "license_expiry", label: "License Expiration", type: "date" },
  { name: "allergies", label: "Allergies", full: true },
  { name: "emergency_contact_name", label: "Emergency Contact Name" },
  { name: "emergency_contact_phone", label: "Emergency Contact Phone", mono: true },
  { name: "emergency_contact_relationship", label: "Emergency Contact Relationship" },
  { name: "pin", label: "Clock-In PIN (blank keeps existing)", mono: true },
  { name: "hourly_rate", label: "Hourly Rate (USD)", type: "number" },
  { name: "initial_pto_hours", label: "Annual PTO Hours", type: "number", default: 40 },
  { name: "status", label: "Status", type: "select", options: ["active", "inactive"], default: "active" },
];

function PayrollDialog({ open, onClose, employee, onSaved }) {
  const [ssn, setSsn] = useState(""); const [bank, setBank] = useState("");
  const [routing, setRouting] = useState(""); const [dob, setDob] = useState("");
  const [revealed, setRevealed] = useState(false);
  const [busy, setBusy] = useState(false); const [saving, setSaving] = useState(false);
  useEffect(() => { if (!open) { setSsn(""); setBank(""); setRouting(""); setDob(""); setRevealed(false); } }, [open]);
  const reveal = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/employees/${employee.id}/sensitive`);
      setSsn(data.ssn || ""); setBank(data.bank_account || ""); setRouting(data.routing_number || ""); setDob(data.dob || "");
      setRevealed(true); toast.success("Fields decrypted — audit entry created");
    } catch (e) { toast.error(e.response?.data?.detail || "Reveal failed"); } finally { setBusy(false); }
  };
  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/employees/${employee.id}/sensitive`, { ssn, bank_account: bank, routing_number: routing, dob });
      toast.success("Encrypted & saved"); onClose(); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="payroll-dialog" className="sm:max-w-md">
        <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><Lock className="w-4 h-4 text-pink-700" /> Payroll & Banking · {employee?.name}</DialogTitle></DialogHeader>
        <div className="p-3 bg-pink-50 border border-pink-200 rounded-lg mb-3 flex items-start gap-2">
          <ShieldCheck className="w-4 h-4 text-pink-700 shrink-0 mt-0.5" />
          <p className="text-xs text-pink-900">AES-256-GCM encrypted at rest. Reveal & save are audited.</p>
        </div>
        <div className="space-y-3">
          {[["SSN", ssn, setSsn, "ssn-input"], ["Bank Account #", bank, setBank, "bank-input"],
            ["Routing #", routing, setRouting, "routing-input"]].map(([lbl, v, sv, id]) => (
            <div key={id}><Label className="text-xs">{lbl}</Label>
              <Input data-testid={id} type={revealed ? "text" : "password"} value={v} onChange={(e) => sv(e.target.value)} className="font-mono" /></div>
          ))}
          <div><Label className="text-xs">Date of Birth</Label>
            <Input data-testid="dob-input" type={revealed ? "date" : "password"} value={dob} onChange={(e) => setDob(e.target.value)} className="font-mono" /></div>
        </div>
        <DialogFooter className="gap-2">
          <Button data-testid="reveal-btn" variant="outline" onClick={reveal} disabled={busy} className="gap-1.5">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : revealed ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            {revealed ? "Values shown" : "Reveal existing"}
          </Button>
          <Button data-testid="payroll-save" onClick={save} disabled={saving} className="bg-pink-700 hover:bg-pink-800">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Encrypt & save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Employees() {
  const { isAdmin } = useAuth();
  const [items, setItems] = useState([]);
  const [payrollFor, setPayrollFor] = useState(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [resetResult, setResetResult] = useState(null);
  const [resetTarget, setResetTarget] = useState(null);
  const [ptoGrantFor, setPtoGrantFor] = useState(null);

  const reload = useCallback(async () => {
    const [emps, staff] = await Promise.all([
      api.get("/employees").then((r) => r.data).catch(() => []),
      api.get("/timeclock/staff").then((r) => r.data).catch(() => []),
    ]);
    const balances = Object.fromEntries(staff.map((s) => [s.id, s.pto_balance]));
    setItems(emps.map((e) => ({ ...e, pto_balance: balances[e.id] || null })));
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const columns = [
    { name: "name", label: "Name", render: (r) => (
      <div>
        <span className="font-medium text-slate-800">{r.name}</span>
        {r.pin_hash && <span title="Has clock-in PIN" className="ml-1.5 inline-flex items-center text-pink-700"><KeyRound className="w-3 h-3" /></span>}
        {r.title && <div className="text-xs text-slate-400">{r.title}</div>}
      </div>
    ) },
    { name: "employee_role", label: "Role" },
    { name: "department", label: "Dept" },
    { name: "hire_date", label: "Hired", mono: true },
    { name: "hourly_rate", label: "Rate", render: (r) => r.hourly_rate ? <span className="font-mono text-xs">${(r.hourly_rate || 0).toFixed(2)}/hr</span> : <span className="text-slate-300">—</span> },
    { name: "pto_balance", label: "PTO", render: (r) => r.pto_balance ? <span className="font-mono text-xs text-emerald-700">{r.pto_balance.available}h</span> : <span className="text-slate-300">—</span> },
    { name: "license_expiry", label: "Lic Exp", mono: true },
    { name: "emergency_contact_name", label: "Emergency", render: (r) => r.emergency_contact_name
      ? <div><div className="text-xs">{r.emergency_contact_name}</div><div className="text-[10px] text-slate-400 font-mono">{r.emergency_contact_phone}</div></div>
      : <span className="text-slate-300">—</span> },
    { name: "status", label: "Status", render: (r) => <StatusBadge status={r.status} /> },
    ...(isAdmin ? [{ name: "reset_pin", label: "", render: (r) => (
      <Button data-testid={`reset-pin-${r.id}`} variant="ghost" size="sm"
        className="h-8 gap-1 text-amber-700 hover:bg-amber-50"
        onClick={() => resetPin(r)}>
        <KeyRound className="w-3.5 h-3.5" /> Reset PIN
      </Button>
    ) }] : []),
    ...(isAdmin ? [{ name: "pto_grant", label: "", render: (r) => (
      <Button data-testid={`pto-grant-${r.id}`} variant="ghost" size="sm"
        className="h-8 gap-1 text-emerald-700 hover:bg-emerald-50"
        onClick={() => setPtoGrantFor(r)}>
        <Plane className="w-3.5 h-3.5" /> Bump PTO
      </Button>
    ) }] : []),
    ...(isAdmin ? [{ name: "payroll", label: "", render: (r) => (
      <Button data-testid={`payroll-${r.id}`} variant="ghost" size="sm" className="h-8 gap-1 text-pink-700 hover:bg-pink-50"
        onClick={() => setPayrollFor(r)}><Lock className="w-3.5 h-3.5" /> Payroll</Button>
    ) }] : []),
  ];

  const resetPin = (row) => setResetTarget(row);

  return (
    <div>
      <PageHeader testid="employees-header" title="Employee Directory"
        subtitle="Master file: role, department, license, hire date, PIN, PTO, emergency contact, allergies, and encrypted payroll.">
        {isAdmin && (
          <Button data-testid="onboard-employee-button" onClick={() => setWizardOpen(true)}
            className="bg-pink-700 hover:bg-pink-800 gap-1.5 h-9">
            <Sparkles className="w-4 h-4" /> Onboard Employee
          </Button>
        )}
      </PageHeader>
      <DataManager testidPrefix="employees" endpoint="employees" fields={fields} columns={columns}
        items={items} reload={reload} emptyIcon={Users} emptyTitle="No employees yet" />
      <p className="text-xs text-slate-500 mt-3">Employees log in at <a href="/clockin" target="_blank" rel="noreferrer" className="text-pink-700 font-mono underline">/clockin</a> to clock in, edit their profile, request PTO, and download handbook / insurance docs.</p>
      <PayrollDialog open={!!payrollFor} employee={payrollFor} onClose={() => setPayrollFor(null)} onSaved={reload} />
      <OnboardingWizard open={wizardOpen} onClose={() => setWizardOpen(false)} onCreated={reload} />
      <ResetPinDialog data={resetResult} onClose={() => setResetResult(null)} />
      <ResetPinFormDialog employee={resetTarget}
        onClose={() => setResetTarget(null)}
        onSaved={(data) => { setResetTarget(null); setResetResult(data); reload(); }} />
      <PtoGrantDialog employee={ptoGrantFor} onClose={() => setPtoGrantFor(null)} onSaved={reload} />
    </div>
  );
}

function PtoGrantDialog({ employee, onClose, onSaved }) {
  const [hours, setHours] = useState("");
  const [effective, setEffective] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (employee) {
      setHours(String(employee.initial_pto_hours || 40));
      setEffective(employee.hire_date
        ? new Date().getFullYear() + employee.hire_date.slice(4)
        : new Date().toISOString().slice(0, 10));
      setNote("");
    }
  }, [employee]);
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/timeclock/staff/${employee.id}/pto-grant`, {
        annual_hours: parseFloat(hours), effective_date: effective, note,
      });
      toast.success(`Annual PTO ${data.old_hours}h → ${data.new_hours}h`);
      onClose(); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  if (!employee) return null;
  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="pto-grant-dialog" className="sm:max-w-md">
        <DialogHeader><DialogTitle className="font-heading flex items-center gap-2">
          <Plane className="w-4 h-4 text-emerald-700" /> Anniversary PTO · {employee.name}
        </DialogTitle></DialogHeader>
        <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg mb-3 text-xs text-emerald-900">
          Set this employee's new <b>annual PTO grant</b>. Balance rolls over on each hire-date anniversary
          {employee.hire_date && <> (<span className="font-mono">{employee.hire_date}</span>)</>}.
          The change is audit-logged and applies from the effective date onward.
        </div>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">New annual PTO hours *</Label>
            <Input data-testid="pto-grant-hours" type="number" step="1" min="0" value={hours}
              onChange={(e) => setHours(e.target.value)} className="font-mono" />
            <div className="text-[11px] text-slate-500 mt-1">
              Currently <b className="font-mono">{employee.initial_pto_hours || 0}h/yr</b>.
            </div>
          </div>
          <div>
            <Label className="text-xs">Effective date</Label>
            <Input data-testid="pto-grant-effective" type="date" value={effective}
              onChange={(e) => setEffective(e.target.value)} />
          </div>
          <div>
            <Label className="text-xs">Reason / note (optional)</Label>
            <Input data-testid="pto-grant-note" value={note} placeholder="e.g. Year 3 anniversary bump"
              onChange={(e) => setNote(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="pto-grant-save" onClick={save} disabled={saving}
            className="bg-emerald-700 hover:bg-emerald-800">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save & audit"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ResetPinFormDialog({ employee, onClose, onSaved }) {
  const [pin, setPin] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);
  const [showRandom, setShowRandom] = useState(false);
  useEffect(() => { if (employee) { setPin(""); setConfirm(""); setShowRandom(false); } }, [employee]);
  const submit = async () => {
    if (!/^\d{4,8}$/.test(pin)) { toast.error("PIN must be 4–8 digits"); return; }
    if (pin !== confirm) { toast.error("PINs don't match — retype the confirmation"); return; }
    setSaving(true);
    try {
      const { data } = await api.post(`/timeclock/staff/${employee.id}/reset-pin`, { pin });
      toast.success(`New PIN set for ${data.name}`);
      onSaved(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Reset failed"); }
    finally { setSaving(false); }
  };
  const randomize = async () => {
    if (!window.confirm(`Generate a random 4-digit PIN for ${employee.name}? It will be shown once.`)) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/timeclock/staff/${employee.id}/reset-pin`, {});
      setShowRandom(false); onSaved(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Reset failed"); }
    finally { setSaving(false); }
  };
  if (!employee) return null;
  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm" data-testid="reset-pin-form">
        <DialogHeader><DialogTitle className="flex items-center gap-2">
          <KeyRound className="w-4 h-4 text-amber-600" /> Reset PIN · {employee.name}
        </DialogTitle></DialogHeader>
        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-900 mb-3">
          Enter the PIN this employee will use to clock in, then retype it to confirm. Any active
          kiosk sessions for this employee will be signed out and the reset is audit-logged.
          Employees cannot reset their own PIN — only an admin can.
        </div>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">New PIN (4–8 digits) *</Label>
            <Input data-testid="reset-pin-new" type="password" inputMode="numeric" pattern="[0-9]*"
              maxLength={8} value={pin} autoComplete="new-password"
              onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
              className="font-mono text-lg tracking-widest text-center" />
          </div>
          <div>
            <Label className="text-xs">Confirm new PIN *</Label>
            <Input data-testid="reset-pin-confirm" type="password" inputMode="numeric" pattern="[0-9]*"
              maxLength={8} value={confirm} autoComplete="new-password"
              onChange={(e) => setConfirm(e.target.value.replace(/\D/g, ""))}
              className={`font-mono text-lg tracking-widest text-center ${
                confirm && pin !== confirm ? "border-rose-300" : ""}`} />
            {confirm && pin !== confirm && (
              <div className="text-[11px] text-rose-600 mt-1">PINs don't match yet.</div>
            )}
          </div>
        </div>
        <DialogFooter className="flex-col sm:flex-col gap-2">
          <div className="flex gap-2 w-full">
            <Button variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
            <Button data-testid="reset-pin-save" onClick={submit} disabled={saving}
              className="flex-1 bg-amber-600 hover:bg-amber-700 text-white">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Set PIN"}
            </Button>
          </div>
          <button type="button" onClick={randomize} disabled={saving}
            className="text-[11px] text-slate-500 hover:text-amber-700 underline">
            or generate a random PIN instead
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ResetPinDialog({ data, onClose }) {
  const copy = async () => {
    try { await navigator.clipboard.writeText(data.pin); toast.success("PIN copied"); }
    catch { toast.error("Clipboard blocked"); }
  };
  if (!data) return null;
  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm" data-testid="reset-pin-result">
        <DialogHeader><DialogTitle className="flex items-center gap-2">
          <KeyRound className="w-4 h-4 text-amber-600" /> New PIN for {data.name}
        </DialogTitle></DialogHeader>
        <div className="p-4 rounded-lg bg-amber-50 border border-amber-200 text-center">
          <div className="text-[10px] uppercase text-amber-800 font-semibold tracking-wide mb-1">One-time reveal</div>
          <div className="text-5xl font-mono font-bold text-amber-900 tracking-widest" data-testid="reset-pin-value">
            {data.pin}
          </div>
        </div>
        <p className="text-xs text-slate-600">
          Share this PIN with {data.name} securely — this dialog is the <b>only</b> place it will
          be shown. Any existing kiosk sessions for this employee have been logged out. The reset
          is recorded in the audit log.
        </p>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Close</Button>
          <Button data-testid="reset-pin-copy" onClick={copy}
            className="bg-amber-600 hover:bg-amber-700 text-white">Copy PIN</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
